import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, TrendingUp, TrendingDown, Database, Users, Settings, BarChart3, LogOut, Menu, X, ChevronDown } from 'lucide-react';

const NAV = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { label: 'Buy Entry', icon: TrendingUp, path: '/buy' },
  { label: 'Sell Entry', icon: TrendingDown, path: '/sell' },
  {
    label: 'Masters', icon: Database, children: [
      { label: 'Script Master', path: '/masters/scripts' },
      { label: 'Customer Master', path: '/masters/customers' },
      { label: 'Brokerage Master', path: '/masters/brokerage' },
    ]
  },
  { label: 'All Transactions', icon: BarChart3, path: '/transactions' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [mastersOpen, setMastersOpen] = useState(location.pathname.startsWith('/masters'));

  const isActive = (p) => location.pathname === p;
  const isMasterActive = () => location.pathname.startsWith('/masters');

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Inter',-apple-system,sans-serif" }}>
      {/* Sidebar */}
      <aside style={{
        width: open ? 230 : 60, transition: 'width 0.25s', flexShrink: 0,
        background: 'linear-gradient(180deg,#0f172a,#1e293b)',
        display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100, overflow: 'hidden'
      }}>
        {/* Logo */}
        <div style={{ padding: '18px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BarChart3 size={18} color="#fff" />
          </div>
          {open && <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 14, letterSpacing: '-0.3px' }}>InvestTrack</div>
            <div style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Portfolio Manager</div>
          </div>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {NAV.map(item => {
            if (item.children) {
              return (
                <div key={item.label}>
                  <button onClick={() => setMastersOpen(o => !o)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px',
                    borderRadius: 8, border: 'none', cursor: 'pointer', background: isMasterActive() ? 'rgba(59,130,246,0.15)' : 'transparent',
                    color: isMasterActive() ? '#93c5fd' : '#94a3b8'
                  }}>
                    <item.icon size={17} style={{ flexShrink: 0 }} />
                    {open && <><span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 500 }}>{item.label}</span>
                      <ChevronDown size={13} style={{ transform: mastersOpen ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} /></>}
                  </button>
                  {open && mastersOpen && item.children.map(c => (
                    <Link key={c.path} to={c.path} style={{ textDecoration: 'none' }}>
                      <div style={{
                        padding: '7px 10px 7px 36px', borderRadius: 8, margin: '1px 0', fontSize: 12,
                        background: isActive(c.path) ? 'rgba(59,130,246,0.2)' : 'transparent',
                        color: isActive(c.path) ? '#93c5fd' : '#64748b',
                        borderLeft: isActive(c.path) ? '2px solid #3b82f6' : '2px solid transparent'
                      }}>{c.label}</div>
                    </Link>
                  ))}
                </div>
              );
            }
            return (
              <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px',
                  borderRadius: 8, margin: '2px 0',
                  background: isActive(item.path) ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: isActive(item.path) ? '#93c5fd' : '#94a3b8',
                  borderLeft: isActive(item.path) ? '2px solid #3b82f6' : '2px solid transparent'
                }}>
                  <item.icon size={17} style={{ flexShrink: 0 }} />
                  {open && <span style={{ fontSize: 13, fontWeight: isActive(item.path) ? 600 : 400 }}>{item.label}</span>}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* User */}
        {open && (
          <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{user?.name}</div>
            <div style={{ color: '#64748b', fontSize: 10, marginBottom: 8, textTransform: 'uppercase' }}>{user?.role}</div>
            <button onClick={() => { logout(); navigate('/login'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
              <LogOut size={13} /> Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <div style={{ marginLeft: open ? 230 : 60, flex: 1, transition: 'margin-left 0.25s' }}>
        {/* Top bar */}
        <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 22px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
          <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
            {open ? <X size={19} /> : <Menu size={19} />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 800 }}>
              {user?.name?.charAt(0)}
            </div>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{user?.name}</span>
          </div>
        </header>
        <main style={{ padding: 22 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
